import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseInfiniteQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import {
  fetchAllOrgCollections,
  fetchProjectCollections,
  fetchCollection,
  fetchCollectionReadme,
  fetchCollectionVersions,
  fetchCollectionReadmeVersions,
  fetchLinkedCollectionDestinations,
  linkDestinationToCollection,
  unlinkDestinationFromCollection,
  createCollection as createCollectionApi,
  updateCollection as updateCollectionApi,
  fetchCollectionChecks,
  createCollectionCheck as createCollectionCheckApi,
  updateCollectionCheck as updateCollectionCheckApi,
  deleteCollectionCheck as deleteCollectionCheckApi,
  fetchCollectionSettings,
  saveCollectionSettings,
  fetchCollectionSnapshots,
  fetchCollectionPushes,
} from '_/api/collections/collections.api';
import { IHistoryItem } from '_/common/history/history.types';
import { IReadmeVersion } from '_/common/types/readme.types';
import { ICollection, ICollectionCheck } from '_/types/collections/collections.types';
import { formatSnapshots, FormattedSnapshot } from '_/utils/snapshots';
import { formatSnapshotHealth } from '_/utils/snapshotUtils';
import { notification } from 'antd';

export const ORG_COLLECTIONS_QUERY_KEY = (orgSlug: string): (string | number)[] => ['collections', 'org', orgSlug];
export const PROJECT_COLLECTIONS_QUERY_KEY = (projectId: string): (string | number)[] => ['collections', 'project', projectId];
export const COLLECTION_QUERY_KEY = (collectionSlug: string, projectId?: string): (string | undefined)[] => [
  'collection',
  collectionSlug,
  projectId,
];
export const COLLECTION_README_QUERY_KEY = (collectionId: string): (string | number)[] => ['collection', collectionId, 'readme'];
export const COLLECTION_VERSIONS_QUERY_KEY = (collectionId: string): (string | number)[] => [
  'collection',
  collectionId,
  'versions',
];
export const COLLECTION_README_VERSIONS_QUERY_KEY = (collectionId: string): (string | number)[] => [
  'collection',
  collectionId,
  'readme',
  'versions',
];
export const COLLECTION_LINKED_DESTINATIONS_QUERY_KEY = (collectionId: string): (string | number)[] => [
  'collection',
  collectionId,
  'destinations',
];
export const COLLECTION_CHECKS_QUERY_KEY = (collectionId: string): (string | number)[] => ['collection', collectionId, 'checks'];
export const COLLECTION_SETTINGS_QUERY_KEY = (collectionId: string): (string | number)[] => [
  'collection',
  collectionId,
  'settings',
];
export const COLLECTION_PUSHES_QUERY_KEY = (collectionId: string): (string | number)[] => ['collection', collectionId, 'pushes'];

export const COLLECTION_SNAPSHOTS_QUERY_KEY = (collectionId: string, filter: string): (string | number)[] => [
  'collection',
  collectionId,
  'snapshots',
  filter,
];

export const useOrgCollections = (orgSlug?: string): UseQueryResult<ICollection[]> => {
  return useQuery({
    queryKey: ORG_COLLECTIONS_QUERY_KEY(orgSlug || ''),
    queryFn: fetchAllOrgCollections,
    enabled: !!orgSlug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCollections = (projectId?: string): UseQueryResult<ICollection[]> => {
  return useQuery({
    queryKey: PROJECT_COLLECTIONS_QUERY_KEY(projectId || ''),
    queryFn: () => fetchProjectCollections(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCollection = (collectionSlug: string | undefined, projectId: string | undefined): UseQueryResult<ICollection> => {
  return useQuery({
    queryKey: COLLECTION_QUERY_KEY(collectionSlug || '', projectId),
    queryFn: () => fetchCollection(collectionSlug!, projectId!),
    enabled: !!collectionSlug && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Alias for backward compatibility
export const useFetchSingleCollection = useCollection;

export const useCollectionReadme = (collectionId: string | undefined): UseQueryResult<string> => {
  return useQuery({
    queryKey: COLLECTION_README_QUERY_KEY(collectionId || ''),
    queryFn: () => fetchCollectionReadme(collectionId!),
    enabled: !!collectionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCollectionVersions = (
  collectionId: string | undefined,
): UseQueryResult<{ items: IHistoryItem<ICollection>[]; cursor: string | null }> => {
  return useQuery({
    queryKey: COLLECTION_VERSIONS_QUERY_KEY(collectionId || ''),
    queryFn: () => fetchCollectionVersions(collectionId!, null, 5),
    enabled: !!collectionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCollectionReadmeVersions = (collectionId: string | undefined): UseQueryResult<IReadmeVersion[]> => {
  return useQuery({
    queryKey: COLLECTION_README_VERSIONS_QUERY_KEY(collectionId || ''),
    queryFn: () => fetchCollectionReadmeVersions(collectionId!),
    enabled: !!collectionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useLinkedCollectionDestinations = (collectionId: string | undefined): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: COLLECTION_LINKED_DESTINATIONS_QUERY_KEY(collectionId || ''),
    queryFn: () => fetchLinkedCollectionDestinations(collectionId!),
    enabled: !!collectionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCreateCollection = (): UseMutationResult<
  ICollection,
  Error,
  { collection: any; projectId: string; collectionReadme: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collection, projectId, collectionReadme }: { collection: any; projectId: string; collectionReadme: string }) =>
      createCollectionApi(collection, projectId, collectionReadme),
    onSuccess: (_, variables) => {
      // Invalidate all org collections queries (for all orgSlugs)
      void queryClient.invalidateQueries({ queryKey: ['collections', 'org'] });
      // Invalidate project collections for this specific project
      void queryClient.invalidateQueries({
        queryKey: PROJECT_COLLECTIONS_QUERY_KEY(variables.projectId),
      });
    },
  });
};

export const useUpdateCollection = (): UseMutationResult<
  void,
  Error,
  { collectionId: string; collection?: any; collectionReadme?: string }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collectionId,
      collection,
      collectionReadme,
    }: {
      collectionId: string;
      collection?: any;
      collectionReadme?: string;
    }) => updateCollectionApi(collectionId, collection, collectionReadme),
    onSuccess: (_, variables) => {
      // Invalidate all org collections queries (for all orgSlugs)
      void queryClient.invalidateQueries({ queryKey: ['collections', 'org'] });
      // Invalidate all project collections queries (for all projectIds)
      void queryClient.invalidateQueries({ queryKey: ['collections', 'project'] });
      // Invalidate collection versions
      void queryClient.invalidateQueries({
        queryKey: COLLECTION_VERSIONS_QUERY_KEY(variables.collectionId),
      });
      if (variables.collectionReadme) {
        void queryClient.invalidateQueries({
          queryKey: COLLECTION_README_QUERY_KEY(variables.collectionId),
        });
        void queryClient.invalidateQueries({
          queryKey: COLLECTION_README_VERSIONS_QUERY_KEY(variables.collectionId),
        });
      }
    },
  });
};

export const useLinkDestinationToCollection = (collectionId: string | undefined): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (destinationId: string) => linkDestinationToCollection(collectionId!, destinationId),
    onSuccess: () => {
      // Invalidate linked destinations for this collection
      void queryClient.invalidateQueries({
        queryKey: COLLECTION_LINKED_DESTINATIONS_QUERY_KEY(collectionId!),
      });
    },
  });
};

export const useUnlinkDestinationFromCollection = (collectionId: string | undefined): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (destinationId: string) => unlinkDestinationFromCollection(collectionId!, destinationId),
    onSuccess: () => {
      // Invalidate linked destinations for this collection
      void queryClient.invalidateQueries({
        queryKey: COLLECTION_LINKED_DESTINATIONS_QUERY_KEY(collectionId!),
      });
    },
  });
};

// Collection Checks
export const useCollectionChecks = (collectionId?: string): UseQueryResult<ICollectionCheck[]> => {
  return useQuery({
    queryKey: COLLECTION_CHECKS_QUERY_KEY(collectionId || ''),
    queryFn: () => fetchCollectionChecks(collectionId!),
    enabled: !!collectionId,
    staleTime: 30 * 1000, // 30 seconds - checks can change frequently
  });
};

export const useCreateCollectionCheck = (collectionId: string): UseMutationResult<void, Error, any> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (collectionCheck: any) => createCollectionCheckApi(collectionCheck),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COLLECTION_CHECKS_QUERY_KEY(collectionId) });
      notification.success({
        message: 'Collection Check Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error creating collection check', error);
      throw error;
    },
  });
};

export const useUpdateCollectionCheck = (
  collectionId: string,
): UseMutationResult<void, Error, { collectionCheckId: string; collectionCheck: any }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ collectionCheckId, collectionCheck }: { collectionCheckId: string; collectionCheck: any }) =>
      updateCollectionCheckApi(collectionCheckId, collectionCheck),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COLLECTION_CHECKS_QUERY_KEY(collectionId) });
      notification.success({
        message: 'Collection Check Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error updating collection check', error);
      notification.error({
        message: error.message || 'Error updating collection check',
      });
    },
  });
};

export const useDeleteCollectionCheck = (collectionId: string): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (collectionCheckId: string) => deleteCollectionCheckApi(collectionCheckId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: COLLECTION_CHECKS_QUERY_KEY(collectionId) });
      notification.success({
        message: 'Collection Check Deleted',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting collection check', error);
      notification.error({
        message: error.message || 'Error deleting collection check',
      });
    },
  });
};

// Collection Settings Hooks
export const useCollectionSettings = (collectionId?: string): UseQueryResult<any> => {
  return useQuery({
    queryKey: COLLECTION_SETTINGS_QUERY_KEY(collectionId || ''),
    queryFn: () => fetchCollectionSettings(collectionId!),
    enabled: !!collectionId,
    staleTime: 60 * 1000, // 1 minute - settings don't change frequently
  });
};

export const useSaveCollectionSettings = (
  collectionId: string,
): UseMutationResult<any, Error, { settings: any; isNew: boolean }> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ settings, isNew }: { settings: any; isNew: boolean }) => saveCollectionSettings(collectionId, settings, isNew),
    onSuccess: (data) => {
      // Update the cache with the returned settings
      queryClient.setQueryData(COLLECTION_SETTINGS_QUERY_KEY(collectionId), data);
      notification.success({
        message: 'Collection Settings Saved',
      });
    },
    onError: (error: any) => {
      console.error('Error saving collection settings', error);
      notification.error({
        message: error.message || 'Error saving collection settings',
      });
      throw error;
    },
  });
};

// Collection Snapshots Hook
export const useCollectionSnapshots = (
  collectionId?: string,
  filter = 'status||in||PENDING_QA,QA,PASSED_QA,IN_PROGRESS,FAILED,FIXING,ESCALATED,FAILED_QA',
): UseInfiniteQueryResult<{ items: FormattedSnapshot[]; cursor?: string }> => {
  return useInfiniteQuery({
    queryKey: COLLECTION_SNAPSHOTS_QUERY_KEY(collectionId || '', filter),
    queryFn: async ({ pageParam }) => {
      const response = await fetchCollectionSnapshots(collectionId!, filter, 50, pageParam);
      // Format snapshots like the Redux action does
      const formattedHealth = formatSnapshotHealth(response.items);
      const formattedSnapshots = formatSnapshots(formattedHealth);
      return { items: formattedSnapshots, cursor: response.cursor };
    },
    getNextPageParam: (lastPage) => lastPage.cursor,
    initialPageParam: undefined as string | undefined,
    enabled: !!collectionId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useCollectionPushes = (collectionId?: string): UseQueryResult<any[]> => {
  return useQuery({
    queryKey: COLLECTION_PUSHES_QUERY_KEY(collectionId || ''),
    queryFn: () => fetchCollectionPushes(collectionId!),
    enabled: !!collectionId,
    staleTime: 30 * 1000,
  });
};
